import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";

import {
  altaChofer,
  buscarNomina,
  editarNumero,
  listaChoferes,
  normalizarNumero,
  reencolarBienvenida,
  setActivo,
  setTractor,
  ticketFaltaPersona,
} from "../services/choferesAutorizados";
import { etiquetaCorta, claseDeEmpresa } from "../shared/empresa";
import { aWhatsapp } from "../shared/telefono";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";

/* "Choferes autorizados" — el sector de Satelital.
 *
 * ── ES DE ELLOS, Y ESO ES MEDIO PUNTO ──────────────────────────────────────
 *
 * Satelital tiene los números de los choferes al día, así que son los que
 * pueden mantener esta lista. Un chofer que cargan acá queda habilitado a
 * escribirle al bot al toque.
 *
 * ── LO QUE SE LLENA SOLO VIENE DE RRHH ──────────────────────────────────────
 *
 * Nombre, legajo, empresa, rol y la fecha de alta NO se tipean: salen de la
 * nómina (por eso el nombre se elige de una lista, no se escribe libre) y del
 * día en que se suma. Satelital sólo pone el número y el tractor. Si el nombre
 * no está en RRHH, no se puede cargar suelto: se abre un ticket para que RRHH
 * lo sume.
 *
 * ── EL CANDADO LO PONE LA BASE ──────────────────────────────────────────────
 *
 * Ver pide `ver.choferes`, editar `cargar.choferes`. Sin la llave las RPC
 * vuelven vacías y las escrituras rebotan, aunque se llamen desde la consola.
 */

const texto = (v) => String(v ?? "").trim();

/* Un campo que se edita en la fila: muestra el valor, y al tocarlo se vuelve un
 * input que guarda al salir o con Enter. Cada uno con su estado, para no
 * arrastrar el de toda la tabla. */
const CampoInline = ({ valor, onGuardar, placeholder, puede, pendKey, onPend }) => {
  const [editando, setEditando] = useState(false);
  const [v, setV] = useState(valor ?? "");
  const [guardando, setGuardando] = useState(false);
  useEffect(() => setV(valor ?? ""), [valor]);

  /* Le avisa al sector si este campo tiene un cambio SIN confirmar, para la
     guarda de "salís sin guardar". Al desmontar, deja de estar pendiente. */
  const sucio = editando && texto(v) !== texto(valor);
  useEffect(() => {
    onPend?.(pendKey, sucio);
    return () => onPend?.(pendKey, false);
  }, [sucio, pendKey, onPend]);

  if (!puede) return <span>{valor || <span className="sin-dato">—</span>}</span>;

  const cerrar = async (commit) => {
    if (!commit || texto(v) === texto(valor)) {
      setV(valor ?? "");
      setEditando(false);
      return;
    }
    setGuardando(true);
    try {
      await onGuardar(texto(v));
      setEditando(false);
    } catch (e) {
      alert("No se pudo guardar. " + (e?.message || ""));
      setV(valor ?? "");
    } finally {
      setGuardando(false);
    }
  };

  if (!editando) {
    return (
      <button type="button" className="th-orden" onClick={() => setEditando(true)}
        title="Tocar para editar">
        {valor ? valor : <span className="sin-dato">{placeholder || "—"}</span>}
        <span className="sub"> ✎</span>
      </button>
    );
  }
  return (
    <input
      autoFocus
      disabled={guardando}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => cerrar(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") cerrar(true);
        if (e.key === "Escape") cerrar(false);
      }}
      placeholder={placeholder}
      style={{ width: "140px", padding: "4px 6px" }}
    />
  );
};

/* El número de WhatsApp del chofer, con el formato de Documentación: link que
 * abre el chat + botón de copiar. El número SALE de Documentos (chofer_documentos,
 * que se sincroniza sola de noche), así que acá se muestra y allá se corrige;
 * el ✎ deja poner un override sólo si hace falta (p. ej. sin número en Documentos). */
const CeldaNumero = ({ numero, fuente, puede, onGuardar, pendKey, onPend }) => {
  const [editando, setEditando] = useState(false);
  const [v, setV] = useState(numero ?? "");
  const [guardando, setGuardando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  useEffect(() => setV(numero ?? ""), [numero]);

  const sucio = editando && texto(v) !== texto(numero);
  useEffect(() => {
    onPend?.(pendKey, sucio);
    return () => onPend?.(pendKey, false);
  }, [sucio, pendKey, onPend]);

  const cerrar = async (commit) => {
    if (!commit || texto(v) === texto(numero)) {
      setV(numero ?? "");
      setEditando(false);
      return;
    }
    setGuardando(true);
    try {
      await onGuardar(texto(v));
      setEditando(false);
    } catch (e) {
      alert("No se pudo guardar. " + (e?.message || ""));
      setV(numero ?? "");
    } finally {
      setGuardando(false);
    }
  };

  if (editando) {
    return (
      <input
        autoFocus
        disabled={guardando}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => cerrar(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") cerrar(true);
          if (e.key === "Escape") cerrar(false);
        }}
        placeholder="11 5555 5555"
        style={{ width: "150px", padding: "4px 6px" }}
      />
    );
  }

  if (!numero) {
    return puede ? (
      <button type="button" className="th-orden" onClick={() => setEditando(true)}
        title="Poner un número (sólo si no está en Documentos)">
        ＋ número
      </button>
    ) : (
      <span className="sin-dato">—</span>
    );
  }

  const dig = String(numero).replace(/\D/g, "");
  const bonito = dig.length >= 13 ? `+54 9 ${dig.slice(3)}` : numero;
  const url = aWhatsapp(numero);
  const copiar = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(dig);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      /* Sin clipboard el botón no promete lo que no puede. */
    }
  };
  return (
    <span className="dato">
      {url ? (
        <a className="dato doc-wa" href={url} target="_blank" rel="noreferrer"
          title="Escribirle por WhatsApp">
          {bonito}
        </a>
      ) : (
        <b>{bonito}</b>
      )}{" "}
      <button type="button" className="doc-copiar" onClick={copiar}
        title={copiado ? "Copiado" : "Copiar el número"}>
        {copiado ? "✓" : "⧉"}
      </button>
      {fuente === "doc" && (
        <span className="sub" title="Sale de Documentos; se corrige ahí"> · Documentos</span>
      )}
      {puede && (
        <button type="button" className="th-orden" onClick={() => setEditando(true)}
          title="Poner otro número (override)">
          {" "}✎
        </button>
      )}
    </span>
  );
};

/* El estado de la bienvenida por WhatsApp de esa fila. Cuando FALLÓ, el que
 * puede editar ve el botón para reintentarla (la vuelve a encolar y el bot la
 * manda en el próximo barrido). Las filas viejas (semilla) quedan 'omitida'. */
const Bienvenida = ({ estado, puede, onReenviar }) => {
  const [reencolando, setReencolando] = useState(false);
  if (estado === "enviada") return <span className="pill verde">✓ enviada</span>;
  if (estado === "pendiente")
    return <span className="pill gris">en cola…</span>;
  if (estado === "falló") {
    if (!puede) return <span className="pill roja">no salió</span>;
    return (
      <button
        type="button"
        className="tk-chip activo"
        disabled={reencolando}
        title="La bienvenida no salió. Reintentar."
        onClick={async () => {
          setReencolando(true);
          try {
            await onReenviar();
          } catch (e) {
            alert("No se pudo reencolar. " + (e?.message || ""));
          } finally {
            setReencolando(false);
          }
        }}
      >
        {reencolando ? "…" : "Mandar bienvenida"}
      </button>
    );
  }
  return <span className="sin-dato">—</span>;
};

const ChoferesAutorizados = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos === null || permisos.includes(p);
  const puedeEditar = puede("cargar.choferes");
  /* Se pide sólo cuando ya se sabe que se puede, como en Nómina: la pared es la
     policy, pero no hay razón para golpearla sin la llave. `null` = todavía no
     llegaron los permisos, y ahí se pide (como el resto del hub). */
  const permitido =
    permisos === null ||
    permisos.includes("ver.choferes") ||
    permisos.includes("cargar.choferes");

  const [filas, setFilas] = useState(undefined);
  const [error, setError] = useState("");
  const [buscar, setBuscar] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [elegido, setElegido] = useState(null);
  const [numeroNuevo, setNumeroNuevo] = useState("");
  const [aviso, setAviso] = useState("");
  const debounce = useRef(null);

  /* CAMBIOS SIN GUARDAR. El sector graba cada campo al confirmarlo (Enter o al
     salir del casillero), así que lo único que se puede perder es un valor
     tipeado a medias o un "Agregar" sin apretar. Eso es lo que se cuida acá. */
  const [pendientes, setPendientes] = useState(() => new Set());
  const marcarPend = useCallback((key, on) => {
    setPendientes((s) => {
      if (on === s.has(key)) return s;
      const n = new Set(s);
      if (on) n.add(key);
      else n.delete(key);
      return n;
    });
  }, []);
  const hayAltaAmedias = !!elegido && numeroNuevo.trim().length > 0;
  const hayPendiente = pendientes.size > 0 || hayAltaAmedias;

  const traer = useCallback(async () => {
    setError("");
    try {
      setFilas(await listaChoferes());
    } catch (e) {
      setError(e?.message || "No se pudo leer la lista.");
      setFilas([]);
    }
  }, []);

  useEffect(() => {
    if (permitido) traer();
  }, [traer, permitido]);

  /* SI TRATA DE SALIR CON ALGO SIN GUARDAR, el navegador pregunta (cerrar la
     pestaña, recargar). Dentro del Hub el banner de arriba ya lo avisa. */
  useEffect(() => {
    if (!hayPendiente) return undefined;
    const avisar = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [hayPendiente]);

  /* IDLE: si pasan 5 min sin tocar nada y hay algo sin guardar, la pestaña de
     Chrome empieza a parpadear para que lo vean. Cualquier actividad —o volver
     a la pestaña— corta el parpadeo y reinicia el reloj. */
  useEffect(() => {
    if (!hayPendiente) return undefined;
    const titulo = document.title;
    let idle;
    let blink;
    let parpadeando = false;
    const parar = () => {
      if (blink) clearInterval(blink);
      blink = null;
      if (parpadeando) {
        document.title = titulo;
        parpadeando = false;
      }
    };
    const arrancar = () => {
      if (parpadeando) return;
      parpadeando = true;
      let on = false;
      blink = setInterval(() => {
        document.title = on ? titulo : "⚠️ Guardá los cambios de choferes";
        on = !on;
      }, 900);
    };
    const reiniciar = () => {
      parar();
      clearTimeout(idle);
      // Sólo cuenta como idle si la pestaña está a la vista sin uso; oculta,
      // parpadear no sirve.
      idle = setTimeout(arrancar, 5 * 60 * 1000);
    };
    const evs = ["mousemove", "keydown", "click", "scroll", "touchstart",
      "visibilitychange", "focus"];
    evs.forEach((e) => window.addEventListener(e, reiniciar, { passive: true }));
    reiniciar();
    return () => {
      evs.forEach((e) => window.removeEventListener(e, reiniciar));
      parar();
      clearTimeout(idle);
      document.title = titulo;
    };
  }, [hayPendiente]);

  /* Autocompletar contra RRHH mientras escriben. Con menos de 2 letras no
     busca; el resultado marca al que YA está para no duplicarlo. */
  useEffect(() => {
    if (elegido) return;
    const q = buscar.trim();
    if (q.length < 2) {
      setSugerencias([]);
      return;
    }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setBuscando(true);
      try {
        setSugerencias(await buscarNomina(q));
      } catch {
        setSugerencias([]);
      } finally {
        setBuscando(false);
      }
    }, 250);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [buscar, elegido]);

  const activos = useMemo(
    () => (filas || []).filter((f) => f.activo).length,
    [filas],
  );

  if (!puede("ver.choferes") && !puede("cargar.choferes")) {
    return <PuertaCerrada />;
  }

  const agregar = async () => {
    if (!elegido) return;
    setAviso("");
    try {
      await altaChofer(elegido.dni, numeroNuevo);
      setElegido(null);
      setBuscar("");
      setNumeroNuevo("");
      setSugerencias([]);
      await traer();
    } catch (e) {
      setAviso("No se pudo agregar. " + (e?.message || ""));
    }
  };

  const pedirTicket = async () => {
    try {
      await ticketFaltaPersona(buscar.trim());
      setAviso(
        `Listo: abrí un ticket para que RRHH sume a «${buscar.trim()}». ` +
          "Cuando esté en la nómina lo vas a poder autorizar.",
      );
    } catch (e) {
      setAviso("No pude abrir el ticket. " + (e?.message || ""));
    }
  };

  /* Con 2+ letras, sin sugerencias y sin haber elegido: esa persona no está en
     RRHH. No se deja cargar suelta — se ofrece el ticket. */
  const faltaEnRrhh =
    !elegido && buscar.trim().length >= 2 && !buscando && sugerencias.length === 0;

  return (
    <div className="docs">
      <h3>
        Choferes autorizados{" "}
        {filas !== undefined && (
          <span className="tk-item-meta">
            {activos} habilitado{activos === 1 ? "" : "s"}
          </span>
        )}
      </h3>

      {/* CAMBIOS SIN GUARDAR: se ve siempre que haya algo a medias, para que
          nadie se vaya creyendo que guardó. */}
      {hayPendiente && (
        <p className="table-escapan" role="status">
          <b>Tenés cambios sin guardar.</b> Confirmá el número o el tractor con
          <b> Enter</b> (o saliendo del casillero), y el alta con <b>Agregar</b>.
        </p>
      )}

      {puedeEditar && (
        <div className="panel-aviso suave" style={{ marginBottom: 14 }}>
          {!elegido ? (
            <>
              <label>
                <b>Agregar chofer</b> — escribí el apellido:
                <br />
                <input
                  type="search"
                  value={buscar}
                  onChange={(e) => setBuscar(e.target.value)}
                  placeholder="Apellido y/o nombre…"
                  style={{ minWidth: 280, padding: "7px 10px", marginTop: 4 }}
                />
              </label>
              {buscando && <p className="sub">buscando…</p>}
              {sugerencias.length > 0 && (
                <ul className="lista-limpia" style={{ marginTop: 6 }}>
                  {sugerencias.map((s) => (
                    <li key={s.dni}>
                      <button
                        type="button"
                        className="tk-chip"
                        disabled={s.ya}
                        title={s.ya ? "Ya está en la lista" : ""}
                        onClick={() => {
                          setElegido(s);
                          setBuscar(s.nombre);
                        }}
                      >
                        {s.nombre}
                        {s.legajo ? ` · leg ${s.legajo}` : ""}
                        {s.ya ? " · ya está" : ""}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {faltaEnRrhh && (
                <div className="table-escapan" style={{ marginTop: 8 }}>
                  <b>«{buscar.trim()}»</b> no aparece en RRHH. No se puede cargar
                  suelto: si tiene que estar, abrí un ticket para que RRHH lo
                  sume.
                  <br />
                  <button type="button" className="tk-chip activo"
                    style={{ marginTop: 6 }} onClick={pedirTicket}>
                    Crear ticket «falta esta persona»
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <p style={{ margin: "0 0 6px" }}>
                <b>{elegido.nombre}</b>
                {elegido.legajo ? ` · legajo ${elegido.legajo}` : ""}
                {elegido.empresa ? ` · ${etiquetaCorta(elegido.empresa)}` : ""}
              </p>
              <label>
                Número de WhatsApp <span className="sub">(opcional — si no, sale de Documentos)</span>:{" "}
                <input
                  value={numeroNuevo}
                  onChange={(e) => setNumeroNuevo(e.target.value)}
                  placeholder="ej. 11 5555 5555 o 2964 15 123456"
                  style={{ minWidth: 220, padding: "6px 8px" }}
                />
              </label>
              {numeroNuevo && (
                <p className="sub">
                  Se va a guardar como <b>{normalizarNumero(numeroNuevo)}</b>
                </p>
              )}
              <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                <button type="button" className="tk-chip activo" onClick={agregar}>
                  Agregar
                </button>
                <button
                  type="button"
                  className="tk-chip"
                  onClick={() => {
                    setElegido(null);
                    setBuscar("");
                    setNumeroNuevo("");
                  }}
                >
                  Cancelar
                </button>
              </div>
            </>
          )}
          {aviso && <p className="sub" style={{ marginTop: 6 }}>{aviso}</p>}
        </div>
      )}

      {error && <p className="aduana-error">{error}</p>}
      {filas === undefined && <p className="sub">cargando…</p>}

      {filas !== undefined && filas.length === 0 && !error && (
        <p className="panel-aviso suave">
          Todavía no hay ningún chofer cargado.
        </p>
      )}

      {filas !== undefined && filas.length > 0 && (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                <th>Chofer</th>
                <th>Legajo</th>
                <th>Empresa</th>
                <th>Número</th>
                <th>Rol</th>
                <th>Alta</th>
                <th>Tractor</th>
                <th>Bienvenida</th>
                {puedeEditar && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.dni} style={{ opacity: f.activo ? 1 : 0.5 }}>
                  <td>
                    {texto(f.nombre) || f.dni}
                    {!f.activo && <span className="pill gris"> baja</span>}
                  </td>
                  <td className="dato">{texto(f.legajo) || "—"}</td>
                  <td>
                    {f.empresa ? (
                      <span className={"pill " + claseDeEmpresa(f.empresa)}>
                        {etiquetaCorta(f.empresa)}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="dato">
                    <CeldaNumero
                      numero={f.numero}
                      fuente={f.tel_fuente}
                      puede={puedeEditar && f.activo}
                      pendKey={"num-" + f.dni}
                      onPend={marcarPend}
                      onGuardar={(v) => editarNumero(f.dni, v).then(traer)}
                    />
                  </td>
                  <td className="sub">Chofer</td>
                  <td className="dato">{texto(f.alta_en) || "—"}</td>
                  <td className="dato">
                    <CampoInline
                      valor={f.tractor}
                      puede={puedeEditar && f.activo}
                      placeholder="interno o patente"
                      pendKey={"trac-" + f.dni}
                      onPend={marcarPend}
                      onGuardar={(v) => setTractor(f.dni, v).then(traer)}
                    />
                  </td>
                  <td>
                    <Bienvenida
                      estado={f.bienvenida_estado}
                      puede={puedeEditar && f.activo}
                      onReenviar={() => reencolarBienvenida(f.dni).then(traer)}
                    />
                  </td>
                  {puedeEditar && (
                    <td>
                      <button
                        type="button"
                        className="tk-chip"
                        onClick={() =>
                          setActivo(f.dni, !f.activo).then(traer)
                        }
                      >
                        {f.activo ? "Dar de baja" : "Reactivar"}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ChoferesAutorizados;
